import { useState, useEffect, useRef } from 'react';
import { Brain, Mic, MicOff, Send, ChevronLeft, Zap, MapPin, AlertCircle, CheckCircle, Sparkles, BarChart2, FileText } from 'lucide-react';

// ── Simulated conversation script ───────────────────────────────────────────
const SCRIPT = [
  {
    role: 'ai',
    text: "Good afternoon, Alen! 👋 Time for your end-of-shift debrief. How did it go in the field today?",
    delay: 0,
  },
  {
    role: 'user',
    text: "Today at Caffe Bar LJ people weren't interested in testing because it was too noisy — there was a concert going on.",
    delay: 0,
    insight: {
      type: 'warning',
      tag: 'External Disruption',
      label: 'Noise / Concert detected at Caffe Bar LJ',
      icon: AlertCircle,
    },
    mapAlert: 'Caffe Bar LJ',
  },
  {
    role: 'ai',
    text: "Understood. Logged as 'External Disruption' at Caffe Bar LJ. Would you suggest we revisit this venue in the morning when it's quieter?",
    delay: 0,
  },
  {
    role: 'user',
    text: "Yes, definitely. The bartender was very friendly though — he already knows our products well.",
    delay: 0,
    insight: {
      type: 'success',
      tag: 'Best Practice',
      label: 'Brand-aligned staff at Caffe Bar LJ',
      icon: Sparkles,
    },
  },
  {
    role: 'ai',
    text: "Excellent! I've tagged Caffe Bar LJ as a 'High-Potential' venue and moved it to morning slots next week. AI Route Optimizer updated automatically. Overall today: 18 DCC — great work! 🏆",
    delay: 0,
  },
];

// ── Waveform bars ────────────────────────────────────────────────────────────
const Waveform = ({ active }) => {
  const BARS = 28;
  return (
    <div className="flex items-center justify-center gap-[3px] h-10">
      {Array.from({ length: BARS }).map((_, i) => (
        <div
          key={i}
          className="rounded-full transition-all"
          style={{
            width: 3,
            background: active ? '#F08D49' : 'rgba(240,141,73,0.25)',
            height: active
              ? `${8 + Math.abs(Math.sin(i * 0.7 + Date.now() * 0.001)) * 28}px`
              : '4px',
            animation: active ? `wave ${0.4 + (i % 5) * 0.12}s ease-in-out infinite alternate` : 'none',
            animationDelay: `${i * 0.04}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes wave {
          from { transform: scaleY(0.4); }
          to   { transform: scaleY(1.0); }
        }
      `}</style>
    </div>
  );
};

// ── Skeleton slide ────────────────────────────────────────────────────────────
const Skeleton = ({ show }) => (
  show ? (
    <div className="space-y-3 mt-4">
      {[90, 70, 80, 55].map((w, i) => (
        <div key={i} className="h-3 rounded-full animate-pulse"
          style={{ width: `${w}%`, background: 'rgba(255,255,255,0.07)', animationDelay: `${i * 0.15}s` }} />
      ))}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {[['DCC Today', '18'], ['Avg Sentiment', '4.7★'], ['Route Score', '94%']].map(([label, val], i) => (
          <div key={i} className="rounded-xl p-3 text-center animate-pulse"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', animationDelay: `${i * 0.2}s` }}>
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider mb-1">{label}</p>
            <p className="text-xl font-bold text-white/20">{val}</p>
          </div>
        ))}
      </div>
    </div>
  ) : null
);

// ── Report card ───────────────────────────────────────────────────────────────
const ReportCard = ({ show }) => (
  show ? (
    <div className="mt-4 space-y-3" style={{ animation: 'fadeUp 0.5s ease forwards' }}>
      <div className="grid grid-cols-3 gap-3">
        {[['DCC Today', '18', '#F08D49'], ['Avg Sentiment', '4.7★', '#4A90E2'], ['Route Score', '94%', '#34D399']].map(([label, val, color], i) => (
          <div key={i} className="rounded-xl p-3 text-center"
            style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${color}33` }}>
            <p className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: `${color}99` }}>{label}</p>
            <p className="text-xl font-bold" style={{ color }}>{val}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl p-4 text-sm text-white/60 leading-relaxed"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <p className="font-semibold text-white/80 mb-1 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#F08D49]" /> AI Daily Summary
        </p>
        Strong performance today. One venue flagged for rescheduling (noise disruption). Brand-aligned staff identified at Caffe Bar LJ — recommended for priority route next shift. Tomorrow's route auto-optimised: 15 venues, morning priority for central Ljubljana.
      </div>
      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  ) : null
);

// ── Insight card ──────────────────────────────────────────────────────────────
const InsightBubble = ({ insight, visible }) => {
  if (!insight || !visible) return null;
  const Icon = insight.icon;
  const isGood = insight.type === 'success';
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-mono"
      style={{
        background: isGood ? 'rgba(52,211,153,0.12)' : 'rgba(251,191,36,0.12)',
        border: `1px solid ${isGood ? 'rgba(52,211,153,0.3)' : 'rgba(251,191,36,0.3)'}`,
        animation: 'insightPop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards',
      }}
    >
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: isGood ? '#34D399' : '#FBD038' }} />
      <div>
        <span className="text-[10px] uppercase tracking-widest opacity-60">{insight.tag} · </span>
        <span className="text-white/80">{insight.label}</span>
      </div>
      <style>{`@keyframes insightPop { from { opacity:0; transform:scale(0.85) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }`}</style>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function AiDebrief() {
  const [messages, setMessages] = useState([]);
  const [scriptIdx, setScriptIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [mapAlert, setMapAlert] = useState(null);
  const [reportSkeleton, setReportSkeleton] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [pulse, setPulse] = useState(true);
  const chatRef = useRef(null);
  const tickRef = useRef(null);

  // Initial AI greeting
  useEffect(() => {
    setTimeout(() => {
      setMessages([{ role: 'ai', text: SCRIPT[0].text }]);
      setScriptIdx(1);
      setPulse(false);
    }, 1200);
  }, []);

  // Auto-scroll
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking]);

  // Animated waveform tick
  useEffect(() => {
    if (recording) {
      tickRef.current = setInterval(() => {}, 80);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [recording]);

  const handleRecord = () => {
    if (scriptIdx >= SCRIPT.length) return;
    const next = SCRIPT[scriptIdx];
    if (next.role !== 'user') return;

    setRecording(true);
    setTimeout(() => {
      setRecording(false);
      // Add user message
      setMessages(prev => [...prev, { role: 'user', text: next.text }]);

      // Show insight if present
      if (next.insight) {
        setTimeout(() => setCurrentInsight(next.insight), 400);
        setTimeout(() => setCurrentInsight(null), 4500);
      }
      if (next.mapAlert) {
        setTimeout(() => setMapAlert(next.mapAlert), 600);
        setTimeout(() => setMapAlert(null), 3000);
      }

      // AI thinking → response
      setThinking(true);
      const aiMsg = SCRIPT[scriptIdx + 1];
      setTimeout(() => {
        setThinking(false);
        if (aiMsg && aiMsg.role === 'ai') {
          setMessages(prev => [...prev, { role: 'ai', text: aiMsg.text }]);
          setScriptIdx(scriptIdx + 2);
        }
      }, 1600);
    }, 2200);
  };

  const handleReport = () => {
    setReportSkeleton(true);
    setTimeout(() => {
      setReportSkeleton(false);
      setReportReady(true);
    }, 2800);
  };

  const allDone = scriptIdx >= SCRIPT.length;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A111F', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* ── Header ── */}
      <header className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(10,17,31,0.95)', backdropFilter: 'blur(14px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <a href="/app" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to Intel
        </a>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 8px #34D399', animation: 'pulse 2s infinite' }} />
          <span className="font-mono text-xs tracking-widest text-white/70 uppercase">Wibe Intel · AI Debrief</span>
        </div>
        <div className="w-20" />
      </header>

      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6 gap-5">

        {/* ── Brain icon ── */}
        <div className="flex flex-col items-center gap-3 pt-2 pb-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(240,141,73,0.2), rgba(74,144,226,0.15))',
                border: '1px solid rgba(240,141,73,0.3)',
                boxShadow: pulse ? '0 0 40px rgba(240,141,73,0.3)' : '0 0 15px rgba(240,141,73,0.15)',
                animation: pulse ? 'brainPulse 1.4s ease-in-out infinite' : 'none',
                transition: 'all 0.5s',
              }}>
              <Brain className="w-8 h-8 text-[#F08D49]" />
            </div>
          </div>
          <div className="text-center">
            <h1 className="font-bold text-white text-lg">The Brain</h1>
            <p className="text-white/40 text-xs font-mono uppercase tracking-widest">AI Debrief · End-of-Shift</p>
          </div>
        </div>

        {/* ── Map alert banner ── */}
        {mapAlert && (
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm"
            style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', animation: 'insightPop 0.3s ease forwards' }}>
            <MapPin className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-red-300 font-mono text-xs">MAP ALERT: <span className="text-white">{mapAlert}</span> flagged as disrupted</span>
            <div className="ml-auto w-2 h-2 rounded-full bg-red-400 animate-ping" />
          </div>
        )}

        {/* ── Chat area ── */}
        <div ref={chatRef} className="flex-1 space-y-4 overflow-y-auto" style={{ maxHeight: 360 }}>
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'ai' && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 mt-1 flex-shrink-0"
                  style={{ background: 'rgba(74,144,226,0.2)', border: '1px solid rgba(74,144,226,0.3)' }}>
                  <Brain className="w-3.5 h-3.5 text-[#4A90E2]" />
                </div>
              )}
              <div className="max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed"
                style={{
                  background: msg.role === 'ai'
                    ? 'rgba(74,144,226,0.1)' : 'rgba(240,141,73,0.12)',
                  border: msg.role === 'ai'
                    ? '1px solid rgba(74,144,226,0.2)' : '1px solid rgba(240,141,73,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: msg.role === 'ai' ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)',
                  borderRadius: msg.role === 'ai' ? '4px 18px 18px 18px' : '18px 4px 18px 18px',
                  animation: 'fadeUp 0.3s ease forwards',
                }}>
                {msg.text}
              </div>
            </div>
          ))}

          {/* Thinking indicator */}
          {thinking && (
            <div className="flex justify-start">
              <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                style={{ background: 'rgba(74,144,226,0.2)', border: '1px solid rgba(74,144,226,0.3)' }}>
                <Brain className="w-3.5 h-3.5 text-[#4A90E2]" />
              </div>
              <div className="px-4 py-3 rounded-2xl flex items-center gap-1.5"
                style={{ background: 'rgba(74,144,226,0.08)', border: '1px solid rgba(74,144,226,0.15)' }}>
                {[0, 0.2, 0.4].map((d, i) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#4A90E2] animate-bounce"
                    style={{ animationDelay: `${d}s` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Insight card ── */}
        <InsightBubble insight={currentInsight} visible={!!currentInsight} />

        {/* ── Waveform & Record button ── */}
        {!allDone && (
          <div className="flex flex-col items-center gap-4 py-4">
            <Waveform active={recording} />
            <button
              onClick={handleRecord}
              disabled={recording || thinking || (scriptIdx < SCRIPT.length && SCRIPT[scriptIdx]?.role !== 'user')}
              className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-40"
              style={{
                background: recording
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : 'linear-gradient(135deg, #F08D49, #e07030)',
                boxShadow: recording
                  ? '0 0 30px rgba(239,68,68,0.5)' : '0 0 20px rgba(240,141,73,0.4)',
                transform: recording ? 'scale(1.1)' : 'scale(1)',
              }}
            >
              {recording ? <MicOff className="w-7 h-7 text-white" /> : <Mic className="w-7 h-7 text-white" />}
            </button>
            <p className="text-white/30 text-xs font-mono">
              {recording ? 'Listening…' : thinking ? 'AI is processing…' : 'Tap to speak'}
            </p>
          </div>
        )}

        {/* ── Generate Report ── */}
        {allDone && !reportSkeleton && !reportReady && (
          <button onClick={handleReport}
            className="w-full py-4 rounded-2xl flex items-center justify-center gap-3 font-semibold text-white transition-all"
            style={{
              background: 'linear-gradient(135deg, #F08D49, #e07030)',
              boxShadow: '0 4px 24px rgba(240,141,73,0.35)',
            }}>
            <BarChart2 className="w-5 h-5" />
            Generate Daily Report
          </button>
        )}

        {/* Skeleton */}
        {reportSkeleton && (
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p className="text-xs font-mono text-white/30 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Zap className="w-3 h-3 text-[#F08D49]" /> AI is generating your daily report…
            </p>
            <Skeleton show={true} />
          </div>
        )}

        {/* Report */}
        {reportReady && (
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(52,211,153,0.2)' }}>
            <p className="text-xs font-mono text-emerald-400 uppercase tracking-widest mb-1 flex items-center gap-2">
              <CheckCircle className="w-3 h-3" /> Report ready · Sent to supervisor
            </p>
            <ReportCard show={true} />
          </div>
        )}

      </div>

      <style>{`
        @keyframes brainPulse {
          0%,100% { transform: scale(1);   box-shadow: 0 0 15px rgba(240,141,73,0.2); }
          50%      { transform: scale(1.06); box-shadow: 0 0 40px rgba(240,141,73,0.45); }
        }
        @keyframes insightPop {
          from { opacity:0; transform:scale(0.9) translateY(10px); }
          to   { opacity:1; transform:scale(1)   translateY(0); }
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
}
